import React, { useEffect, useMemo, useState } from "react";
import { Command } from "cmdk";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import {
    Plus, Home, Users, Activity, Settings2, RefreshCw, Zap, Wallet,
    User as UserIcon, Search as SearchIcon, ListChecks,
} from "lucide-react";

/**
 * CmdK — global command bar (⌘K / Ctrl+K)
 * Groups: Ações · Clientes · Navegar
 */
export default function CmdK({ open, onOpenChange, onNavigate }) {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [query, setQuery] = useState("");
    const [results, setResults] = useState({ vendas: [], influencers: [], leads_parceiros: [] });
    const [loading, setLoading] = useState(false);

    // Debounced global search via /api/search (unified: vendas + influencers + leads)
    useEffect(() => {
        if (!open || !query || query.length < 2) {
            setResults({ vendas: [], influencers: [], leads_parceiros: [] });
            return;
        }
        setLoading(true);
        const t = setTimeout(() => {
            api.get(`/search?q=${encodeURIComponent(query)}&limit=8`)
                .then((r) => setResults(r.data || { vendas: [], influencers: [], leads_parceiros: [] }))
                .catch(() => setResults({ vendas: [], influencers: [], leads_parceiros: [] }))
                .finally(() => setLoading(false));
        }, 200);
        return () => clearTimeout(t);
    }, [query, open]);

    const matches = results.vendas || [];
    const influencersMatches = results.influencers || [];
    const leadsMatches = results.leads_parceiros || [];

    // Reset query when closed
    useEffect(() => {
        if (!open) setTimeout(() => setQuery(""), 200);
    }, [open]);

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.12 }}
                    className="fixed inset-0 z-[80] flex items-start justify-center pt-[10vh] px-4"
                    style={{ backdropFilter: "blur(6px)", background: "rgba(0,0,0,0.4)" }}
                    onClick={() => onOpenChange(false)}
                    data-testid="cmdk-overlay"
                >
                    <motion.div
                        initial={{ y: -8, opacity: 0, scale: 0.98 }}
                        animate={{ y: 0, opacity: 1, scale: 1 }}
                        exit={{ y: -8, opacity: 0, scale: 0.98 }}
                        transition={{ type: "spring", damping: 24, stiffness: 260 }}
                        className="w-full max-w-xl rounded-xl border shadow-2xl overflow-hidden"
                        style={{
                            background: "var(--v2-surface)",
                            borderColor: "var(--v2-border)",
                            color: "var(--v2-foreground)",
                        }}
                        onClick={(e) => e.stopPropagation()}
                        data-testid="cmdk-modal"
                    >
                        <Command shouldFilter={false} className="flex flex-col">
                            <div
                                className="flex items-center gap-2 h-12 px-4 border-b"
                                style={{ borderColor: "var(--v2-border)" }}
                            >
                                <SearchIcon size={16} style={{ color: "var(--v2-muted)" }} />
                                <Command.Input
                                    value={query}
                                    onValueChange={setQuery}
                                    autoFocus
                                    placeholder="Procurar clientes, ações, páginas…"
                                    className="flex-1 outline-none bg-transparent text-[15px] font-body placeholder:text-[color:var(--v2-muted)]"
                                    data-testid="cmdk-input"
                                />
                                <kbd
                                    className="text-[10px] font-mono px-1.5 py-0.5 rounded border"
                                    style={{ borderColor: "var(--v2-border)", color: "var(--v2-muted)" }}
                                >
                                    ESC
                                </kbd>
                            </div>

                            <Command.List className="max-h-[52vh] overflow-y-auto py-2">
                                <Command.Empty className="px-4 py-6 text-center text-[13px]" style={{ color: "var(--v2-muted)" }}>
                                    {loading ? "A carregar…" : query.length < 2 ? "Escreve pelo menos 2 letras para procurar clientes" : "Nenhum resultado"}
                                </Command.Empty>

                                {/* CLIENTES / VENDAS */}
                                {matches.length > 0 && (
                                    <Command.Group heading="Clientes" className="px-2">
                                        <div
                                            className="text-[10px] font-body font-semibold uppercase tracking-widest px-2 py-1"
                                            style={{ color: "var(--v2-muted)" }}
                                        >
                                            Clientes / Vendas
                                        </div>
                                        {matches.map((v) => (
                                            <Command.Item
                                                key={v.id}
                                                value={`cliente-${v.id}`}
                                                onSelect={() => onNavigate(`/clientes?id=${v.id}`)}
                                                className="flex items-center gap-3 h-9 px-2 rounded-md cursor-pointer aria-selected:bg-[color:var(--v2-surface-hover)]"
                                                data-testid={`cmdk-cliente-${v.id}`}
                                            >
                                                <UserIcon size={14} style={{ color: "var(--v2-muted)" }} />
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-[13px] truncate">{v.nome}</div>
                                                    <div className="text-[11px] truncate" style={{ color: "var(--v2-muted)" }}>
                                                        {v.telefone} · {v.concelho || ""} · {v.dataVenda}
                                                    </div>
                                                </div>
                                                <kbd className="text-[10px] font-mono px-1.5 py-0.5 rounded border" style={{ borderColor: "var(--v2-border)", color: "var(--v2-muted)" }}>↵</kbd>
                                            </Command.Item>
                                        ))}
                                    </Command.Group>
                                )}

                                {/* INFLUENCERS */}
                                {influencersMatches.length > 0 && (
                                    <Command.Group heading="Influencers" className="px-2 mt-1">
                                        <div
                                            className="text-[10px] font-body font-semibold uppercase tracking-widest px-2 py-1"
                                            style={{ color: "var(--v2-muted)" }}
                                        >
                                            Influencers
                                        </div>
                                        {influencersMatches.map((i) => (
                                            <Command.Item
                                                key={i.id}
                                                value={`inf-${i.id}`}
                                                onSelect={() => onNavigate(`/influencers`)}
                                                className="flex items-center gap-3 h-9 px-2 rounded-md cursor-pointer aria-selected:bg-[color:var(--v2-surface-hover)]"
                                                data-testid={`cmdk-inf-${i.id}`}
                                            >
                                                <UserIcon size={14} style={{ color: "var(--v2-muted)" }} />
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-[13px] truncate">{i.nome}</div>
                                                    <div className="text-[11px] truncate" style={{ color: "var(--v2-muted)" }}>
                                                        {i.telefone} · {i.tipo || ""} · {i.active === false ? "inactivo" : "activo"}
                                                    </div>
                                                </div>
                                            </Command.Item>
                                        ))}
                                    </Command.Group>
                                )}

                                {/* LEADS PARCEIROS */}
                                {leadsMatches.length > 0 && (
                                    <Command.Group heading="Leads Parceiros" className="px-2 mt-1">
                                        <div
                                            className="text-[10px] font-body font-semibold uppercase tracking-widest px-2 py-1"
                                            style={{ color: "var(--v2-muted)" }}
                                        >
                                            Leads Parceiros
                                        </div>
                                        {leadsMatches.map((l) => (
                                            <Command.Item
                                                key={l.id}
                                                value={`lp-${l.id}`}
                                                onSelect={() => onNavigate(`/leads-parceiros`)}
                                                className="flex items-center gap-3 h-9 px-2 rounded-md cursor-pointer aria-selected:bg-[color:var(--v2-surface-hover)]"
                                                data-testid={`cmdk-lp-${l.id}`}
                                            >
                                                <UserIcon size={14} style={{ color: "var(--v2-muted)" }} />
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-[13px] truncate">{l.nome}</div>
                                                    <div className="text-[11px] truncate" style={{ color: "var(--v2-muted)" }}>
                                                        {l.whatsapp} · {l.estado} · @{l.influencer_slug || "—"}
                                                    </div>
                                                </div>
                                            </Command.Item>
                                        ))}
                                    </Command.Group>
                                )}

                                {/* AÇÕES */}
                                <div className="px-2 mt-1">
                                    <div
                                        className="text-[10px] font-body font-semibold uppercase tracking-widest px-2 py-1"
                                        style={{ color: "var(--v2-muted)" }}
                                    >
                                        Ações
                                    </div>
                                    <ActionItem icon={Plus} label="Nova venda" shortcut="N" onSelect={() => onNavigate("/nova-venda")} testid="cmdk-nova-venda" />
                                    <ActionItem icon={RefreshCw} label="Sincronizar com Kommo" onSelect={() => onNavigate("/configuracoes?section=kommo")} testid="cmdk-sync" />
                                    <ActionItem icon={Zap} label="Testar conexão Kommo" onSelect={() => onNavigate("/configuracoes?section=kommo")} testid="cmdk-test-kommo" />
                                </div>

                                {/* NAVEGAR */}
                                <div className="px-2 mt-1">
                                    <div
                                        className="text-[10px] font-body font-semibold uppercase tracking-widest px-2 py-1"
                                        style={{ color: "var(--v2-muted)" }}
                                    >
                                        Navegar
                                    </div>
                                    <ActionItem icon={Home} label="Hoje" onSelect={() => onNavigate("/hoje")} testid="cmdk-nav-hoje" />
                                    <ActionItem icon={Users} label="Clientes" onSelect={() => onNavigate("/clientes")} testid="cmdk-nav-clientes" />
                                    <ActionItem icon={ListChecks} label="Instalações" onSelect={() => onNavigate("/instalacoes-v2")} testid="cmdk-nav-instalacoes" />
                                    {isAdmin && (
                                        <ActionItem icon={Wallet} label="Financeiro" onSelect={() => onNavigate("/financeiro")} testid="cmdk-nav-financeiro" />
                                    )}
                                    {isAdmin && (
                                        <ActionItem icon={Activity} label="Estado do sistema" onSelect={() => onNavigate("/estado-sistema")} testid="cmdk-nav-estado" />
                                    )}
                                    <ActionItem icon={Settings2} label="Configurações" onSelect={() => onNavigate("/configuracoes")} testid="cmdk-nav-config" />
                                </div>
                            </Command.List>
                        </Command>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

function ActionItem({ icon: Icon, label, shortcut, onSelect, testid }) {
    return (
        <Command.Item
            value={label.toLowerCase()}
            onSelect={onSelect}
            className="flex items-center gap-3 h-9 px-2 rounded-md cursor-pointer aria-selected:bg-[color:var(--v2-surface-hover)]"
            data-testid={testid}
        >
            <Icon size={14} style={{ color: "var(--v2-muted)" }} />
            <span className="flex-1 text-[13px]">{label}</span>
            {shortcut && (
                <kbd className="text-[10px] font-mono px-1.5 py-0.5 rounded border" style={{ borderColor: "var(--v2-border)", color: "var(--v2-muted)" }}>
                    {shortcut}
                </kbd>
            )}
        </Command.Item>
    );
}
